import { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { LOCAL_STORAGE_TYPES } from '@/constants';
import useLocalStorage from '@/hooks/localstorage';
import { useEmailVerificationResend } from '@/hooks/api/auth';
import { UserContainer } from '@/providers';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useDemoMode } from '@/hooks/use-demo-mode';

const RESEND_TIMEOUT = 60;

export const isVerified = (userState: any) =>
  userState?.user &&
  (userState.user.isVerified || userState.user.authMethods?.google);

const VerifyEmailTopbar = () => {
  const { state: userState } = UserContainer.useContainer();
  const verifyEmail = useEmailVerificationResend();
  const { set, get } = useLocalStorage();
  const location = useLocation();
  const { isDemoMode } = useDemoMode();

  const { t } = useTranslation('signup');

  const [isVisible, setIsVisible] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [disableTimer, setDisableTimer] = useState(
    Number(get(LOCAL_STORAGE_TYPES.RESEND_MAIL_TIMEOUT)) ?? RESEND_TIMEOUT
  );

  useEffect(() => {
    const intervalId = setInterval(() => {
      setDisableTimer(prev => {
        const newValue = prev - 1;
        if (newValue === 0) {
          clearInterval(intervalId);
          set(LOCAL_STORAGE_TYPES.RESEND_MAIL_TIMEOUT, 0);
          return 0;
        }
        set(LOCAL_STORAGE_TYPES.RESEND_MAIL_TIMEOUT, newValue);
        return newValue;
      });
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [disableTimer]);

  const handleResendEmail = useCallback(async () => {
    if (disableTimer !== RESEND_TIMEOUT && disableTimer > 0) return;

    setIsResending(true);
    try {
      await verifyEmail();
      toast({
        variant: 'success',
        title: 'Verification email has been resent.',
        description: 'Plese check your inbox and spam folders',
        duration: 5000,
      });

      setDisableTimer(RESEND_TIMEOUT);
      set(LOCAL_STORAGE_TYPES.RESEND_MAIL_TIMEOUT, RESEND_TIMEOUT);
      setDisableTimer(RESEND_TIMEOUT);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to resend verification email.',
        description: 'Please try again.',
      });
    } finally {
      setIsResending(false);
    }
  }, [disableTimer, verifyEmail]);

  if (!isVisible) return null;
  if (location.pathname.includes('/verify-email')) return null;
  if (location.pathname.includes('/onboarding')) return null;
  if (isVerified(userState)) return null;
  if (isDemoMode) return null;

  return (
    <div className="sticky top-0 z-50 bg-primary py-3">
      <div className="max-w-screen-2xl mx-auto">
        <div className="">
          <span className="flex items-center justify-center gap-2 text-primary-400-foreground text-sm">
            {t(
              'verify-email.banner-message',
              'Please verify your email address to finish account setup.'
            )}
            <Button
              className="px-3 py-0 text-sm text-white pl-0 pr-0 font-bold data-[disabled]:text-white disabled:opacity-80 disabled:cursor-not-allowed"
              variant="link"
              onClick={handleResendEmail}
              disabled={isResending || disableTimer > 0}
            >
              {isResending
                ? t('verify-email.sending', 'Sending...')
                : t('verify-email.resend-button', 'Resend verification email')}
              {disableTimer > 0 && ` (${disableTimer}s)`}
            </Button>
            <button
              className="size-4 relative"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              onClick={() => setShowTooltip(!showTooltip)}
              aria-label={t(
                'verify-email.spam-notice',
                "Check the spam folder if you can't find it and please mark the email as not spam."
              )}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              {showTooltip && (
                <div className="absolute w-96 text-xs bg-white text-gray-800 px-4 py-2 rounded shadow-lg z-30 text-wrap">
                  {t(
                    'verify-email.spam-notice',
                    "Check the spam folder if you can't find it and please mark the email as not spam."
                  )}
                </div>
              )}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailTopbar;
